import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import ValuesetPicker from '@/components/ValuesetPicker';
import {
  ArrowLeft, Save, Sparkles, Loader2, Layers, Search, Check, Database
} from 'lucide-react';
import { toast } from 'sonner';

export default function SchemaImportPage() {
  const { schemaId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [search, setSearch] = useState('');
  // Track selections: { [attribute_id]: { selected: bool, valueset_id: string|null, is_required: bool } }
  const [selections, setSelections] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const r = await api.get(`/schemas/${schemaId}/available-attributes`);
      setData(r.data);
      // Initialize selections from existing mappings
      const init = {};
      (r.data.attributes || []).forEach(a => {
        init[a.attribute_id] = {
          selected: a.in_schema,
          valueset_id: a.valueset_id || null,
          is_required: a.is_required || false,
        };
      });
      setSelections(init);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [schemaId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleAttr = (id) => {
    setSelections(prev => ({ ...prev, [id]: { ...prev[id], selected: !prev[id]?.selected } }));
  };

  const setValueset = (attrId, vsId) => {
    setSelections(prev => ({ ...prev, [attrId]: { ...prev[attrId], valueset_id: vsId === '__none__' ? null : vsId } }));
  };

  const setRequired = (attrId, val) => {
    setSelections(prev => ({ ...prev, [attrId]: { ...prev[attrId], is_required: val } }));
  };

  const toggleEntity = (entityId) => {
    const attrs = (data?.attributes || []).filter(a => a.entity_id === entityId);
    const allSelected = attrs.every(a => selections[a.attribute_id]?.selected);
    setSelections(prev => {
      const next = { ...prev };
      attrs.forEach(a => { next[a.attribute_id] = { ...next[a.attribute_id], selected: !allSelected }; });
      return next;
    });
  };

  const handleAISuggest = async () => {
    if (!data?.schema) return;
    setSuggesting(true);
    try {
      const r = await api.post('/schemas/ai-suggest', { name: data.schema.name });
      const suggested = r.data.suggested_attributes || [];
      if (suggested.length > 0) {
        setSelections(prev => {
          const next = { ...prev };
          // First deselect all
          Object.keys(next).forEach(k => { next[k] = { ...next[k], selected: false }; });
          // Then select AI suggestions
          suggested.forEach(s => {
            if (next[s.attribute_id]) {
              next[s.attribute_id] = {
                selected: true,
                valueset_id: s.valueset_id || next[s.attribute_id].valueset_id,
                is_required: s.is_required || false,
              };
            }
          });
          return next;
        });
        toast.success(`AI selected ${suggested.length} attributes`);
      } else {
        toast.info('AI did not suggest specific attributes');
      }
    } catch { toast.error('AI suggestion failed'); }
    finally { setSuggesting(false); }
  };

  const handleSave = async () => {
    const items = Object.entries(selections)
      .filter(([_, v]) => v.selected)
      .map(([id, v]) => ({ attribute_id: id, valueset_id: v.valueset_id, is_required: v.is_required }));

    // First remove all existing mappings, then bulk import
    setSaving(true);
    try {
      // Remove attributes no longer selected
      const currentlyMapped = (data?.attributes || []).filter(a => a.in_schema).map(a => a.attribute_id);
      const nowSelected = new Set(items.map(i => i.attribute_id));
      for (const attrId of currentlyMapped) {
        if (!nowSelected.has(attrId)) {
          await api.delete(`/schemas/${schemaId}/attributes/${attrId}`).catch(() => {});
        }
      }
      // Update existing mappings
      for (const item of items) {
        const existing = (data?.attributes || []).find(a => a.attribute_id === item.attribute_id && a.in_schema);
        if (existing) {
          await api.put(`/schemas/${schemaId}/attributes/${item.attribute_id}`, {
            valueset_id: item.valueset_id,
            is_required: item.is_required,
          }).catch(() => {});
        }
      }
      // Add new ones
      const newItems = items.filter(i => !currentlyMapped.includes(i.attribute_id));
      if (newItems.length > 0) {
        await api.post(`/schemas/${schemaId}/bulk-import`, { items: newItems });
      }
      toast.success('Schema updated');
      navigate(`/schemas/${schemaId}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  // Group attributes by entity
  const grouped = useMemo(() => {
    if (!data) return [];
    const entities = {};
    (data.attributes || []).forEach(a => {
      const eid = a.entity_id || 'unknown';
      if (!entities[eid]) {
        entities[eid] = { entity_id: eid, entity_name: a.entity_name || 'Unknown', attributes: [] };
      }
      if (!search || a.name.toLowerCase().includes(search.toLowerCase()) || a.entity_name?.toLowerCase().includes(search.toLowerCase())) {
        entities[eid].attributes.push(a);
      }
    });
    return Object.values(entities).filter(e => e.attributes.length > 0).sort((a, b) => a.entity_name.localeCompare(b.entity_name));
  }, [data, search]);

  const selectedCount = Object.values(selections).filter(v => v.selected).length;
  const totalCount = (data?.attributes || []).length;

  if (loading) {
    return (<div className="min-h-screen bg-white"><Navbar /><div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div></div>);
  }

  return (
    <div className="min-h-screen bg-white" data-testid="schema-import-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/schemas/${schemaId}`)} className="h-7 px-2 rounded-none text-[#4B5563]" data-testid="back-to-schema">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> {data?.schema?.name || 'Schema'}
          </Button>
          <span className="text-[#9CA3AF]">/</span>
          <span className="text-sm font-medium text-[#0A0A0A]">Import Attributes</span>
        </div>

        <div className="border-b border-[#E5E7EB] pb-5 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A] mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Import Attributes
              </h1>
              <p className="text-sm text-[#4B5563]">
                Select attributes to include in <strong>{data?.schema?.name}</strong>. {selectedCount} of {totalCount} selected.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleAISuggest} disabled={suggesting} className="rounded-none text-xs h-8" data-testid="ai-suggest-import-btn">
                {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                {suggesting ? 'AI selecting...' : 'AI Select Attributes'}
              </Button>
              <Button onClick={handleSave} disabled={saving} className="rounded-none bg-[#002FA7] hover:bg-[#002585] text-xs h-8" data-testid="save-import-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saving...' : `Save (${selectedCount})`}
              </Button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter attributes..." className="pl-8 h-8 text-sm rounded-none" data-testid="import-search" />
        </div>

        {/* Attribute selector by entity */}
        <div className="border border-[#E5E7EB]" data-testid="import-selector">
          {grouped.map(entity => {
            const entityAttrs = entity.attributes;
            const allEntitySelected = entityAttrs.every(a => selections[a.attribute_id]?.selected);
            const someSelected = entityAttrs.some(a => selections[a.attribute_id]?.selected);

            return (
              <div key={entity.entity_id}>
                <div className="flex items-center gap-3 px-4 py-2.5 bg-[#F9FAFB] border-b border-[#E5E7EB]" data-testid={`import-entity-${entity.entity_id}`}>
                  <Checkbox
                    checked={allEntitySelected}
                    onCheckedChange={() => toggleEntity(entity.entity_id)}
                    className="h-4 w-4 rounded-none"
                  />
                  <Layers className="w-4 h-4 text-[#002FA7]" strokeWidth={1.5} />
                  <span className="text-sm font-semibold text-[#0A0A0A] flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{entity.entity_name}</span>
                  <span className="text-[10px] text-[#9CA3AF]">{entityAttrs.filter(a => selections[a.attribute_id]?.selected).length}/{entityAttrs.length}</span>
                </div>
                {entityAttrs.map(attr => {
                  const sel = selections[attr.attribute_id] || {};
                  return (
                    <div key={attr.attribute_id} className={`flex items-center gap-3 px-4 py-2 pl-12 border-b border-[#E5E7EB] last:border-b-0 transition-colors ${sel.selected ? 'bg-white' : 'bg-zinc-50/50'}`} data-testid={`import-attr-${attr.attribute_id}`}>
                      <Checkbox
                        checked={!!sel.selected}
                        onCheckedChange={() => toggleAttr(attr.attribute_id)}
                        className="h-3.5 w-3.5 rounded-none"
                      />
                      <span className={`text-sm flex-1 ${sel.selected ? 'text-[#0A0A0A]' : 'text-[#9CA3AF]'}`}>{attr.name}</span>
                      {attr.group_name && <span className="text-[10px] text-[#9CA3AF]">{attr.group_name}</span>}
                      <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-600" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{attr.data_type}</span>
                      {sel.selected && attr.data_type === 'code' && (
                        <ValuesetPicker
                          value={sel.valueset_id}
                          onChange={v => setValueset(attr.attribute_id, v)}
                          valuesets={data?.valuesets || []}
                          triggerClassName="h-7 text-[10px] w-44 shrink-0"
                          testId={`vs-${attr.attribute_id}`}
                        />
                      )}
                      {sel.selected && (
                        <Checkbox checked={!!sel.is_required} onCheckedChange={v => setRequired(attr.attribute_id, !!v)} className="h-3.5 w-3.5 rounded-none" data-testid={`req-${attr.attribute_id}`} />
                      )}
                      {sel.selected && <span className="text-[10px] text-[#9CA3AF] w-8">Req</span>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
